import React, { useState, useEffect } from 'react';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import Header from '../header/header';
import { ProgressIndicator } from './ProgressIndicator.jsx';
import { UserInfoStep } from './steps/UserInfoStep.jsx';
import { EmailVerificationStep } from './steps/EmailVerificationStep.jsx';
import { PlanSelectionStep } from './steps/PlanSelectionStep.jsx';
import { AccountConnectionStep } from './steps/AccountConnectionStep.jsx';
import { DataSyncingStep } from './steps/DataSyncingStep.jsx';

import { STORAGE_KEY } from '../../constants/onBoarding';

import './onboarding.css';

const steps = [
  { id: 1, title: 'User Information', component: UserInfoStep },
  { id: 2, title: 'Email Verification', component: EmailVerificationStep },
  { id: 3, title: 'Plan Selection', component: PlanSelectionStep },
  { id: 4, title: 'Account Connection', component: AccountConnectionStep },
  { id: 5, title: 'Data Syncing', component: DataSyncingStep }
];

export const OnboardingWizard = () => {
  const {token} = useSelector((state) => state.auth);
  const navigate = useNavigate();
  // LocalStorage backed progress
  const readProgress = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : { currentStep: 1, maxStep: 1 };
    } catch {
      return { currentStep: 1, maxStep: 1 };
    }
  };
  const writeProgress = (p) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(p)); } catch {}
  };
  const initial = readProgress();
  // On mount, sanity check stored progress
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const stateParam = urlParams.get('state');
    let isReauth = false;
    try {
      if (stateParam) {
        const parsed = JSON.parse(decodeURIComponent(stateParam));
        isReauth = parsed?.reauth === true;
      }
    } catch (e) {}

    // If reauth mode, jump directly to step 4 (Account Connection)
    if (isReauth) {
      setCurrentStep(4);
      setMaxStep(4);
      return;
    }

    const p = readProgress();
    const c = Math.min(Math.max(p.currentStep || 1, 1), steps.length);
    const m = Math.min(Math.max(p.maxStep || 1, 1), steps.length);

    // Check for invite URL parameters
    const invitedEmail = urlParams.get('invitedEmail');
    const warehouseReferral = urlParams.get('warehouseReferral');

    // If invite parameters are present and user is logged in, redirect to step 1
    if (invitedEmail && warehouseReferral) {
      if (!token) {
        setCurrentStep(1);
        setMaxStep(1);
        writeProgress({ currentStep: 1, maxStep: 1 });
        return;
      }
      navigate('/', { replace: true });
    }

    if (c !== currentStep) setCurrentStep(c);
    if (m !== maxStep) setMaxStep(m);
    if (c !== (p.currentStep || 1) || m !== (p.maxStep || 1)) {
      writeProgress({ currentStep: c, maxStep: m });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [currentStep, setCurrentStep] = useState(initial.currentStep || 1);
  const [maxStep, setMaxStep] = useState(initial.maxStep || 1);
  const [formData, setFormData] = useState({
    userInfo: {},
    selectedPlan: null,
    connectedAccounts: { amazon: false, walmart: false }
  });

  const nextStep = (data) => {
    if (data) {
      setFormData(prev => ({ ...prev, ...data }));
    }
    if (currentStep < steps.length) {
      const nextStepNumber = currentStep + 1;
      setCurrentStep(nextStepNumber);
      writeProgress({ currentStep: nextStepNumber, maxStep: Math.max(maxStep, nextStepNumber) });
    }
  };

  const goToStep = (stepNumber) => {
    if (stepNumber >= 1 && stepNumber <= steps.length) {
      setCurrentStep(stepNumber);
      writeProgress({ currentStep: stepNumber, maxStep: Math.max(maxStep, stepNumber) });
    }
  };

  const CurrentStepComponent = steps[currentStep - 1].component;

  return (

     <div>
       <Header login={true} onTop={true} isTransparent={true} />
       <div className="onboarding-wizard">
        <div className="onboarding-content">
          <div className="onboarding-container">
            {/* Header */}
            <div className="header">
              <h1 className="title">
                Welcome to <span className="brand">MarketOS</span>
              </h1>
              <p className="subtitle">
                Let's get your account set up in just a few steps
              </p>
            </div>

            {/* Progress Indicator */}
            <ProgressIndicator
              steps={steps}
              currentStep={currentStep}
            />

            {/* Step Content */}
            <div className="step-content">
              <CurrentStepComponent
                onNext={nextStep}
                goToStep={goToStep}
                data={formData}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
